import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "./fordon";
import { faqSchema } from "@/components/HubEditorial";
import { AdSlot } from "@/components/AdSlot";
import { RelatedLinks } from "@/components/RelatedLinks";
import { PriceIndexChart } from "@/components/PriceIndexChart";
import { DeferredIsland } from "@/components/DeferredIsland";

const loadPosition = () => import("@/components/islands/PriceIndexPositionIsland");
const loadEmbed = () => import("@/components/islands/PriceIndexEmbedIsland");

import { getPriceIndex, type PriceIndexMonth } from "@/lib/price-index.functions";
import { getMarketStats, type MarketRow } from "@/lib/market-stats.functions";
import { fetchProducts } from "@/lib/shopify";
import { buildBaskets, buildCategoryPrices } from "@/lib/accessory-index";


const URL = "https://www.akhusbilfritid.se/prisindex";
const TITLE = "AK Prisindex – prisutveckling husbil & husvagn månad för månad";
const DESC =
  "Vårt eget prisindex för husbil och husvagn: medianpris, prisspann och månadsförändring räknat på annonser vi följer dagligen — plus vad tillbehören kostar.";

const FAQ = [
  {
    q: "Vad är AK Prisindex?",
    a: "En månadsvis mätning av prisnivån på begagnade husbilar och husvagnar, räknad på de annonser vi själva följer dygnet runt. Varje månad sparas som en egen mätpunkt i databasen och räknas aldrig om i efterhand. Första publicerade månaden sätts till index 100, och därefter visar indexet hur prisnivån rört sig i procent.",
  },
  {
    q: "Hur skiljer indexet sig från prisstatistiken?",
    a: "Prisstatistiken svarar på vad en husbil kostar just nu per segment, fabrikat och årsmodell. Indexet svarar på om priserna är på väg upp eller ner. Statistiken räknas om vid varje sidvisning, indexet är låst per månad — det är skillnaden mellan en termometer och en kurva.",
  },
  {
    q: "Varför medianpris och inte snittpris?",
    a: "En integrerad husbil för 1,6 miljoner drar snittet flera hundra tusen kronor, medianen står still. Vi redovisar därför median plus spannet mellan 15:e och 85:e percentilen, som är den nivå de flesta affärer faktiskt görs på. Månader med färre än fem objekt publiceras inte alls.",
  },
  {
    q: "Är detta annonspris eller slutpris?",
    a: "Grundserien är annonsnivå, alltså vad marknaden begär. När vi har verifierade slutpriser för månaden redovisas de i egen kolumn. Historiskt ligger slutpriset hos handlare omkring tio procent under annonspriset, och det är den justering vårt värderingsverktyg använder.",
  },
  {
    q: "Vad är tillbehörsindexet?",
    a: "Vad det kostar att utrusta fordonet, räknat på våra egna butikspriser. Korgarna sätts ihop av de billigaste produkterna som matchar respektive funktion, så summan följer butiken automatiskt. Saknas en produkt räknas den inte in — därför står antalet poster alltid vid varje korg.",
  },
];

export const Route = createFileRoute("/prisindex")({
  loader: async () => {
    const [index, stats, products] = await Promise.all([
      getPriceIndex(),
      getMarketStats(),
      fetchProducts(30).catch(() => []),
    ]);
    return {
      index,
      stats,
      baskets: buildBaskets(products),
      categoryPrices: buildCategoryPrices(products),
    };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "prisindex husbil, prisutveckling husbil 2026, husvagnspriser utveckling, sjunker husbilspriserna, vad kostar tillbehör husbil",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "AK Prisindex — prisutveckling husbil & husvagn" },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "AK Prisindex – prisutveckling husbil och husvagn",
          description: DESC,
          url: URL,
          temporalCoverage: "2026/..",
          spatialCoverage: "Sverige",
          variableMeasured: ["Medianpris", "Prisspann (P15–P85)", "Månadsförändring", "Index (bas 100)"],
          creator: { "@type": "Organization", name: "AK Husbil & Fritid" },
          license: "https://www.akhusbilfritid.se/prisindex",
          distribution: [
            {
              "@type": "DataDownload",
              encodingFormat: "text/csv",
              contentUrl: "https://www.akhusbilfritid.se/api/public/prisindex.csv",
            },
            {
              "@type": "DataDownload",
              encodingFormat: "application/json",
              contentUrl: "https://www.akhusbilfritid.se/api/public/prisindex.json",
            },
          ],
          isAccessibleForFree: true,
          inLanguage: "sv-SE",

        }),
      },
    ],
  }),
  component: Page,
});

const kr = (v: number) => `${Math.round(v).toLocaleString("sv-SE")} kr`;
const monthLabel = (m: string) =>
  new Date(m).toLocaleDateString("sv-SE", { year: "numeric", month: "long" });

function Delta({ value }: { value: number | null }) {
  if (value == null) return <span className="text-muted-foreground">Basmånad</span>;
  const up = value > 0;
  return (
    <span className={up ? "text-accent" : value < 0 ? "text-muted-foreground" : ""}>
      {up ? "+" : ""}
      {value.toLocaleString("sv-SE")} %
    </span>
  );
}

function IndexTable({ rows, label }: { rows: PriceIndexMonth[]; label: string }) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-3xl border border-border bg-card">
      <table className="w-full min-w-[40rem] text-sm">
        <caption className="px-5 pt-5 text-left font-display text-lg">{label}</caption>
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th scope="col" className="px-5 py-3">Månad</th>
            <th scope="col" className="px-5 py-3">Index</th>
            <th scope="col" className="px-5 py-3">Medianpris</th>
            <th scope="col" className="px-5 py-3">Vanligt spann</th>
            <th scope="col" className="px-5 py-3">Förändring</th>
            <th scope="col" className="px-5 py-3">Objekt</th>
          </tr>
        </thead>
        <tbody>
          {[...rows].reverse().map((r) => (
            <tr key={r.month} className="border-b border-border/60 last:border-0">
              <th scope="row" className="px-5 py-3 text-left font-medium capitalize">
                {monthLabel(r.month)}
              </th>
              <td className="px-5 py-3 font-medium">{r.index.toLocaleString("sv-SE")}</td>
              <td className="px-5 py-3 font-medium text-accent">{kr(r.medianPrice)}</td>
              <td className="px-5 py-3 text-muted-foreground">
                {kr(r.p15Price)} – {kr(r.p85Price)}
              </td>
              <td className="px-5 py-3">
                <Delta value={r.changePct} />
              </td>
              <td className="px-5 py-3 text-muted-foreground">{r.n}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SimpleTable({
  caption,
  firstHeader,
  rows,
}: {
  caption: string;
  firstHeader: string;
  rows: MarketRow[];
}) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-3xl border border-border bg-card">
      <table className="w-full min-w-[34rem] text-sm">
        <caption className="px-5 pt-5 text-left font-display text-lg">{caption}</caption>
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th scope="col" className="px-5 py-3">{firstHeader}</th>
            <th scope="col" className="px-5 py-3">Median</th>
            <th scope="col" className="px-5 py-3">Vanligt spann</th>
            <th scope="col" className="px-5 py-3">Objekt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-border/60 last:border-0">
              <th scope="row" className="px-5 py-3 text-left font-medium">{r.label}</th>
              <td className="px-5 py-3 font-medium text-accent">{kr(r.medianPrice)}</td>
              <td className="px-5 py-3 text-muted-foreground">
                {kr(r.lowPrice)} – {kr(r.highPrice)}
              </td>
              <td className="px-5 py-3 text-muted-foreground">{r.n}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Page() {
  const { index, stats, baskets, categoryPrices } = Route.useLoaderData();
  const husbil = index.months.filter((m) => m.category === "husbil");
  const husvagn = index.months.filter((m) => m.category === "husvagn");

  return (
    <>
      <PageHeader
        label="Data · AK Prisindex"
        title={
          <>
            Prisindex för <span className="font-italic-display text-accent">husbil och husvagn</span>
          </>
        }
        sub={
          index.baseMonth
            ? `Månadsvis prisnivå räknad på de annonser vi följer dygnet runt. Basmånad ${monthLabel(index.baseMonth)} = index 100. Nya mätpunkter läggs till varje månad och räknas aldrig om i efterhand.`
            : "Månadsvis prisnivå räknad på de annonser vi följer dygnet runt. Första mätpunkten publiceras så snart underlaget är tillräckligt."
        }
      />

      <section className="container-edge pt-8 md:pt-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(["husbil", "husvagn"] as const).map((category) => {
            const latest = index.latest[category];
            if (!latest) return null;
            return (
              <div key={category} className="rounded-3xl border border-border bg-card p-6">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Median {category}
                </p>
                <p className="mt-2 font-display text-3xl leading-none text-accent">
                  {kr(latest.medianPrice)}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Index {latest.index.toLocaleString("sv-SE")} · {latest.n} objekt ·{" "}
                  <Delta value={latest.changePct} />
                </p>
              </div>
            );
          })}
          <div className="rounded-3xl border border-border bg-card p-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Metod</p>
            <p className="mt-2 font-display text-3xl leading-none">Median</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Spann = 15:e till 85:e percentilen. Månader under fem objekt publiceras inte.
            </p>
          </div>
          <div className="rounded-3xl border border-border bg-card p-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Senast uppdaterad</p>
            <p className="mt-2 font-display text-3xl leading-none">
              {new Date(index.updatedAt).toLocaleDateString("sv-SE")}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Mätpunkten skrivs om varje natt fram till månadsskiftet, sedan låses den.
            </p>
          </div>
        </div>
      </section>

      <section className="container-edge space-y-6 pt-12 md:pt-16">
        <div className="grid gap-6 lg:grid-cols-2">
          <PriceIndexChart rows={husbil} label="Medianpris husbil, månad för månad" />
          <PriceIndexChart rows={husvagn} label="Medianpris husvagn, månad för månad" />
        </div>
        <IndexTable rows={husbil} label="Prisindex husbil, månad för månad" />
        <IndexTable rows={husvagn} label="Prisindex husvagn, månad för månad" />
        {!index.months.length && (
          <p className="rounded-3xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Indexet startar så snart en månad har minst fem tolkbara objekt. Vi publicerar hellre
            ingen kurva än en kurva som vilar på tre annonser.
          </p>
        )}
        {index.months.length > 0 && (
          <div className="grid gap-6 lg:grid-cols-2">
            <DeferredIsland
              load={loadPosition}
              componentProps={{ latest: index.latest }}
              minHeight={320}
              label="Laddar marknadsposition…"
            />
            <div className="rounded-3xl border border-border bg-card p-6">
              <h2 className="font-display text-lg">Öppen data — använd serien själv</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Hela indexet är fritt att citera och bygga vidare på, så länge du anger
                akhusbilfritid.se som källa. Serien uppdateras samtidigt som sidan.
              </p>
              <p className="mt-4 flex flex-wrap gap-3 text-sm">
                <a
                  href="/api/public/prisindex.csv"
                  className="inline-flex min-h-11 items-center rounded-full border border-border px-5 font-medium transition hover:border-accent/50 hover:text-accent"
                >
                  Ladda ner CSV
                </a>
                <a
                  href="/api/public/prisindex.json"
                  className="inline-flex min-h-11 items-center rounded-full border border-border px-5 font-medium transition hover:border-accent/50 hover:text-accent"
                >
                  Öppna JSON-API
                </a>
              </p>
            </div>
          </div>
        )}
        {index.months.length > 0 && (
          <DeferredIsland load={loadEmbed} minHeight={280} label="Laddar inbäddningskod…" />
        )}
      </section>



      <section className="container-edge pt-10">
        <AdSlot variant="in-article" />
      </section>

      <section className="container-edge space-y-6 pt-12 md:pt-16">
        <h2 className="font-display text-2xl md:text-3xl">Var i marknaden ligger nivån just nu?</h2>
        <p className="max-w-3xl text-[0.975rem] leading-relaxed text-muted-foreground">
          Indexet visar rörelsen, tabellerna nedan visar nivån. De räknas om vid varje sidvisning på
          samma underlag och är hämtade från{" "}
          <Link to="/prisstatistik-husbil" className="font-medium text-accent underline decoration-accent/40">
            prisstatistiken
          </Link>
          .
        </p>
        <SimpleTable caption="Nivå per segment" firstHeader="Segment" rows={stats.segments} />
        <SimpleTable caption="Nivå per fabrikat" firstHeader="Fabrikat" rows={stats.brands} />
        <SimpleTable caption="Nivå per årsmodell" firstHeader="Årsmodell" rows={stats.years} />

      </section>

      {baskets.length > 0 && (
        <section className="container-edge pt-12 md:pt-16">
          <h2 className="font-display text-2xl md:text-3xl">Tillbehörsindex — vad kostar det att utrusta?</h2>
          <p className="mt-3 max-w-3xl text-[0.975rem] leading-relaxed text-muted-foreground">
            Fordonet är den stora posten, men utrustningen avgör om det går att använda i november.
            Korgarna nedan är räknade på våra egna butikspriser, med den billigaste produkten som
            uppfyller varje funktion. Summan uppdateras automatiskt när priserna i butiken ändras.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {baskets.map((b) => (
              <div key={b.id} className="rounded-3xl border border-border bg-card p-6">
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-display text-lg">{b.label}</h3>
                  <p className="font-display text-xl text-accent">{kr(b.total)}</p>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{b.purpose}</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {b.items.map((i) => (
                    <li key={i.handle} className="flex justify-between gap-4">
                      <Link
                        to="/butik/$handle"
                        params={{ handle: i.handle }}
                        className="min-w-0 truncate underline decoration-border hover:decoration-accent"
                      >
                        {i.title}
                      </Link>
                      <span className="shrink-0 text-muted-foreground">{kr(i.price)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-muted-foreground">{b.items.length} poster i korgen</p>
              </div>
            ))}
          </div>

          {categoryPrices.length > 0 && (
            <div className="mt-6 overflow-x-auto rounded-3xl border border-border bg-card">
              <table className="w-full min-w-[34rem] text-sm">
                <caption className="px-5 pt-5 text-left font-display text-lg">
                  Prisnivå per tillbehörskategori i vår butik
                </caption>
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th scope="col" className="px-5 py-3">Kategori</th>
                    <th scope="col" className="px-5 py-3">Från</th>
                    <th scope="col" className="px-5 py-3">Median</th>
                    <th scope="col" className="px-5 py-3">Upp till</th>
                    <th scope="col" className="px-5 py-3">Artiklar</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryPrices.map((c) => (
                    <tr key={c.type} className="border-b border-border/60 last:border-0">
                      <th scope="row" className="px-5 py-3 text-left font-medium">{c.type}</th>
                      <td className="px-5 py-3 text-muted-foreground">{kr(c.lowest)}</td>
                      <td className="px-5 py-3 font-medium text-accent">{kr(c.median)}</td>
                      <td className="px-5 py-3 text-muted-foreground">{kr(c.highest)}</td>
                      <td className="px-5 py-3 text-muted-foreground">{c.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="container-edge pt-12 md:pt-16">
        <div className="prose-editorial max-w-3xl space-y-5 text-[0.975rem] leading-relaxed text-muted-foreground">
          <h2 className="font-display text-2xl text-foreground">Så använder du indexet i en förhandling</h2>
          <p>
            Ett index är bara användbart om det ändrar vad du gör. Tre situationer där kurvan spelar
            roll: du ska sälja och undrar om du ska vänta, du ska köpa och undrar om du ska pressa,
            eller du ska byta in och undrar hur mycket mellanskillnaden rör sig. Faller indexet två
            månader i rad förlorar den som väntar med försäljningen pengar varje månad, för fordonet
            skrivs av samtidigt som nivån sjunker. Stiger indexet inför våren — vilket är det normala
            mönstret i mars till juni — är det köparen som har mest att vinna på att agera tidigt.
          </p>
          <p>
            Titta alltid på spannet, inte bara medianen. Ett brett spann betyder att gruppen
            innehåller både slitna objekt och nästan nya, och då är det skicket som avgör priset mer
            än marknadsläget. Ett smalt spann betyder att marknaden är enig, och då är det svårare att
            argumentera sig till ett avdrag. Kolumnen med antal objekt är lika viktig: en månad med
            åtta objekt säger något om riktningen, inte om exakta kronor.
          </p>
          <h2 className="font-display text-2xl text-foreground">Varför vi låser varje månad</h2>
          <p>
            De flesta prisstatistiksidor räknar om hela sin historik varje gång underlaget ändras.
            Det gör kurvan omöjlig att lita på, eftersom förra månadens värde kan ha varit ett annat
            när du läste det. Vi sparar i stället varje månads mätpunkt som en egen rad, med antal
            objekt och percentiler som de såg ut då. Under pågående månad uppdateras raden varje natt
            eftersom underlaget växer, men vid månadsskiftet står den still för alltid.
          </p>
          <p>
            Underlaget är annonser vi själva följer, inte inköpt data. Det gör materialet mindre än
            en branschdatabas men helt transparent: varje objekt i indexet har funnits som en riktig
            annons med ett riktigt pris. När vi har verifierade slutpriser för en månad redovisas de
            separat i{" "}
            <Link to="/salda" className="font-medium text-accent underline decoration-accent/40">
              arkivet över sålda fordon
            </Link>
            , där varje rad har det pris affären faktiskt gjordes på.
          </p>
          <h2 className="font-display text-2xl text-foreground">Från index till ditt eget fordon</h2>
          <p>
            Indexet säger var marknaden ligger. För att komma till en siffra för just ditt fordon
            behövs mil, fukt, däckens ålder och servicehistorik. Det räknar{" "}
            <Link to="/vardera-husbil" className="font-medium text-accent underline decoration-accent/40">
              värderingsverktyget
            </Link>{" "}
            fram, med varje multiplikator synlig. Ska du sälja går{" "}
            <Link to="/salja-husbil" className="font-medium text-accent underline decoration-accent/40">
              säljguiden
            </Link>{" "}
            igenom skillnaden mellan privat annons, förmedling och inbyte i kronor, och vill du hålla
            koll på nivån utan att räkna själv finns{" "}
            <Link to="/prisstatistik-husbil" className="font-medium text-accent underline decoration-accent/40">
              prisstatistiken
            </Link>{" "}
            med median per segment, fabrikat och årsmodell.
          </p>
          <h2 className="font-display text-2xl text-foreground">Vanliga frågor</h2>
          <dl className="space-y-4">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-medium text-foreground">{f.q}</dt>
                <dd className="mt-1">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="container-edge pt-12">
        <RelatedLinks
          keyId="prisindex"
          text="prisindex husbil husvagn prisutveckling marknadspris median värdering sälja tillbehör butik"
          currentHref="/prisindex"
        />
        <div className="pt-10">
          <AdSlot variant="multiplex" label="Relaterat innehåll" />
        </div>
      </section>
    </>
  );
}
